export type FacebookLoadType = "Search" | "Scrape";
export type FBVerifyStatus = "Pending" | "Verified";
export type SocialDiscoveryFailMode = "WebsiteIsSpa" | "CantVerifyFacebook" | "AgeGated" | "SiteUnavailable";

export interface SocialSetData {
  instagramHandle?: string | null;
  instagramValidatedAt?: string | null;
  facebookPageId?: string | null;
  facebookPageName?: string | null;
  facebookPageUrl?: string | null;
  facebookValidatedAt?: string | null;
  facebookLoadType?: FacebookLoadType | null;
  tikTokHandle?: string | null;
}

export type LocationOrigin = "Manual" | "LocationBackfill" | "CreatorVisit" | "NearbyDiscovery";
export type LocationTagKind = "Location" | "Person" | "Event";

export interface LocationData {
  id: string;
  customerId: string;
  name: string;
  icon?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  googlePlaceId?: string | null;
  website?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Missing/legacy rows are treated as Location. */
  tagKind?: LocationTagKind | null;
  /** Null/undefined means Origin was never set (pre-backfill legacy rows). */
  origin?: LocationOrigin | null;
  /** Null when automated discovery had no tracked failure mode. */
  failMode?: SocialDiscoveryFailMode | null;
  unverifiedFBHandle?: string | null;
  fbVerifyStatus?: FBVerifyStatus | null;
  instagramLocationId?: string | null;
  socialSet: SocialSetData;
  deletedDate?: string | null;
  createdDate?: string | null;
  _etag?: string | null;
}

export enum InstagramPhotosToTag {
  None = "None",
  First = "First",
  All = "All",
  Manual = "Manual",
}

export interface InstagramTagPlacementData {
  mediaId: string;
  x: number;
  y: number;
}

export interface InstagramTagSettingsData {
  /** Null defaults to First for posts that stored InstagramTag as {}. */
  photosToTag?: InstagramPhotosToTag | null;
  placements?: InstagramTagPlacementData[] | null;
}

export interface SocialPostLocationTagSettingsData {
  locationId: string;
  addCaptionMention: boolean;
  instagramTag?: InstagramTagSettingsData | null;
}

export interface PostLocationDisplayItem {
  locationId: string;
  locationName: string;
  website?: string | null;
  instagramHandle?: string | null;
  facebookPageId?: string | null;
  facebookPageName?: string | null;
  facebookPageUrl?: string | null;
  tikTokHandle?: string | null;
  icon?: string | null;
}

export interface TaggableLocation {
  locationId: string;
  name: string;
  iconUrl?: string | null;
  instagramHandle?: string | null;
  facebookPageId?: string | null;
  facebookPageName?: string | null;
  facebookPageUrl?: string | null;
  tikTokHandle?: string | null;
  /** Missing/legacy rows are treated as Location. */
  tagKind?: LocationTagKind | null;
  canMention: boolean;
  canInstagramTag: boolean;
  canSetInstagramLocation: boolean;
}

export interface FacebookPageSearchResult {
  pageId: string;
  name: string;
  url?: string | null;
  iconUrl?: string | null;
  iconId?: string | null;
  address?: string | null;
  instagramLocationId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  selectionToken?: string | null;
  validatedAt?: string | null;
  loadType?: FacebookLoadType | null;
}

export interface FacebookPageSelectionPayload {
  pageId: string;
  pageName: string;
  pageUrl?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  validatedAt?: string | null;
  loadType?: FacebookLoadType | null;
}

export interface InstagramValidationResult {
  isValid: boolean;
  username: string;
  name?: string | null;
  iconId?: string | null;
  message?: string | null;
  validatedAt?: string | null;
}

export interface WebsiteSocialLinkCandidate {
  url: string;
  handle: string;
  evidence: string;
  rank: number;
  score: number;
}

export interface WebsiteSocialDiscoveryResult {
  requestedUrl: string;
  finalUrl: string;
  pagesChecked: string[];
  facebook: WebsiteSocialLinkCandidate[];
  instagram: WebsiteSocialLinkCandidate[];
  tiktok: WebsiteSocialLinkCandidate[];
  websiteIsSpa?: boolean;
  siteUnavailable?: boolean;
  /** Best apple-touch-icon / sized rel=icon from the homepage when present; import only if social icons miss. */
  websiteIconUrl?: string | null;
  /** Ordered icon candidates (max 3); try in order until one imports. */
  websiteIconUrls?: string[] | null;
}

export interface SaveLocationRequest {
  eTag?: string | null;
  name: string;
  instagramHandle?: string | null;
  instagramValidatedAt?: string | null;
  iconId?: string | null;
  facebookPageSelectionToken?: string | null;
  facebookPageSelection?: FacebookPageSelectionPayload | null;
  clearFacebookPage?: boolean;
  tikTokHandle?: string | null;
  /** Only sent when the user picked a place this session. Blank means "leave the stored place alone". */
  googlePlaceId?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** When true, persist failMode (including null). When omitted/false, leave the stored value alone. */
  updateFailMode?: boolean;
  failMode?: SocialDiscoveryFailMode | null;
  /** True when the user chose Manually Enter. Slack-only; not persisted. */
  isManualMode?: boolean;
  tagKind?: LocationTagKind;
  /** Facebook handle from website discovery when Page resolve failed. */
  unverifiedFBHandle?: string | null;
}
